import { getFirebaseAuthToken } from '../../../utils/firebaseAuthToken';

// Audio utilities for handling protected storage URLs
const FETCH_AUDIO_ENDPOINT = 'https://us-central1-guidesofia-access-control.cloudfunctions.net/fetchAudio';

const getAuthToken = async (currentUser) => {
  return getFirebaseAuthToken(currentUser);
};

const getHostname = (value) => {
  try {
    return new URL(value).hostname;
  } catch {
    return '';
  }
};

/**
 * Converts a protected Firebase Storage audio URL to an accessible one
 * @param {string} protectedUrl - The original protected storage URL
 * @returns {Promise<string>} - A URL that can be used to access the audio
 */
export const getAccessibleAudioUrl = async (protectedUrl, currentUser = null) => {
  if (!protectedUrl) {
    return protectedUrl;
  }

  const hostname = getHostname(protectedUrl);

  // Firebase download URLs already carry a token and can be used directly.
  if (hostname.includes('firebasestorage.googleapis.com')) {
    return protectedUrl;
  }

  // Non-Firebase URLs should also pass through unchanged.
  if (hostname !== 'storage.googleapis.com') {
    return protectedUrl;
  }

  try {
    console.log('Fetching protected audio URL:', protectedUrl);

    const token = await getAuthToken(currentUser);
    const proxyUrl = `${FETCH_AUDIO_ENDPOINT}?url=${encodeURIComponent(protectedUrl)}`;

    const response = await fetch(proxyUrl, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => '');
      throw new Error(`Audio fetch failed: ${response.status} ${errorText}`.trim());
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    console.log('Created accessible audio blob URL');
    return objectUrl;

  } catch (error) {
    console.error('Error converting protected audio URL:', error);
    throw error;
  }
};

/**
 * Creates an audio element that can handle protected URLs
 * @param {string} audioUrl - The audio URL (protected or public)
 * @param {Object} options - Additional options for the audio element
 * @returns {Promise<HTMLAudioElement>} - Configured audio element
 */
export const createAudioElement = async (audioUrl, options = {}) => {
  const accessibleUrl = await getAccessibleAudioUrl(audioUrl);

  const audio = document.createElement('audio');
  audio.src = accessibleUrl;

  // Apply any options
  if (options.controls !== undefined) audio.controls = options.controls;
  if (options.autoplay !== undefined) audio.autoplay = options.autoplay;
  if (options.loop !== undefined) audio.loop = options.loop;
  if (options.preload !== undefined) audio.preload = options.preload;

  return audio;
};

/**
 * Preloads an audio file and returns duration information
 * @param {string} audioUrl - The audio URL to analyze
 * @returns {Promise<Object>} - Object with duration and other metadata
 */
export const getAudioMetadata = async (audioUrl) => {
  return new Promise(async (resolve, reject) => {
    try {
      const audio = await createAudioElement(audioUrl, { preload: 'metadata' });

      audio.onloadedmetadata = () => {
        resolve({
          duration: audio.duration,
          src: audio.src
        });
      };

      audio.onerror = (error) => {
        console.error('Error loading audio metadata:', error);
        reject(new Error('Failed to load audio metadata'));
      };

      // Trigger loading
      audio.load();

    } catch (error) {
      reject(error);
    }
  });
};
